import type { StringFunctionWithPlaceholders } from "@1js/localization";
import { isDevLocalization } from "@1js/localization";
import * as React from "react";
import { useStringProvider } from "../StringProviderContext";
import { FormatInternal } from "./FormatInternal";
import type { StringResolverFunc } from "./formatJSX";

export interface FormatWithProviderProps<K extends string> {
  text: StringFunctionWithPlaceholders<K>;
  placeholders: { readonly [key in K]: JSX.Element | string };
  count?: number;
  wrapper?: (element: string | JSX.Element, index: number) => JSX.Element;
}

/**
 * Formats a string with placeholders, with JSX elements as values for the placeholders.
 * The difference from ordinary `Format` component is that context-based string lookup
 * is used here instead of relying on global string provider instance.
 */
export function FormatWithProvider<K extends string>(
  props: FormatWithProviderProps<K>
): JSX.Element {
  const { lookupString } = useStringProvider();
  const resolveString: StringResolverFunc<
    K,
    StringFunctionWithPlaceholders<K>
  > = (string, placeholders) =>
    isDevLocalization()
      ? string(placeholders)
      : lookupString(string.key)(placeholders);
  return <FormatInternal resolveString={resolveString} {...props} />;
}
